import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { MileDrivenData } from '../interfaces/ride-data/mile-drive.interface';
import { EventStatisticData } from '../interfaces/ride-data/event-statistic.interface';
import { formatDate } from '../utilities/utility-functions';

export interface RideDetailFilterDto {
  driverId?: bigint;
  dateOfService?: string;
  locationId?: number;
  page?: number;
  pageSize?: number;
}

export interface DashboardChartsRequest {
  locationId: number;
}

export interface DashboardStatisticRequest {
  locationId: number;
}

export interface GpsPlaybackRideGeoPoint {
  latitude: number;
  longitude: number;
  time: string; // Format: "YYYY-MM-DD HH:mm:ss"
}

export interface GpsPlaybackRideStop {
  id: number;
  latitude: number;
  longitude: number;
  type: string; // "Home" or "School"
}

export interface GpsPlaybackDriverInfo {
  firstName: string;
  lastName: string;
  plateNumber: string;
  avatar: string; // Base64 encoded image string
}

export interface GpsPlaybackData {
  rideId: number;
  dateOfService: string;
  actualStartTimeUtc: string;
  actualEndTimeUtc: string;
  type: string; // "OUTBOUND" or "RETURN"
  rideStatusId: number;
  rideStops: GpsPlaybackRideStop[];
  driverInfo: GpsPlaybackDriverInfo;
  rideGeoPoints: GpsPlaybackRideGeoPoint[];
}

@Injectable({
  providedIn: 'root',
})
export class RidesService {
  private readonly baseUrl = environment.mainapiUrl;

  constructor(private readonly http: HttpClient) {}

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse) {
    console.error('API Error:', error);

    if (error.status === 401) {
      console.warn('Unauthorized access - user may need to re-authenticate');
    } else if (error.status === 403) {
      console.warn('Forbidden - user may not have proper permissions');
    } else if (error.status === 0) {
      console.error('Network error - check API connectivity');
    }

    return throwError(() => error);
  }

  /**
   * Sync rides list by driver
   */
  public syncRidesListByDriver(driverId: bigint, dateOfService?: string): Observable<any> {
    if (dateOfService) {
      return this.syncRidesListByDriverAndDate(driverId, formatDate(dateOfService));
    }
    return this.http
      .get(`${this.baseUrl}/rides/drivers/${driverId}`)
      .pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Sync rides list by driver and date
   */
  public syncRidesListByDriverAndDate(
    driverId: bigint,
    dateOfService: string,
  ): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/rides/drivers/${driverId}/date`, {
        params: { dateOfService },
      })
      .pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Get miles driven data for charts
   */
  public getMilesDriven(): Observable<MileDrivenData> {
    return this.http
      .get<MileDrivenData>(`${this.baseUrl}/rides/miles-driven`)
      .pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Get events statistics data for charts
   */
  public getEventsStatistics(): Observable<EventStatisticData> {
    return this.http
      .get<EventStatisticData>(`${this.baseUrl}/rides/events-statistics`)
      .pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Get ride details with filter
   */
  public getRideDetails(filter: any): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/rides/get-details`, filter)
      .pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Get ride coordinates for mapping
   */
  public getRideCoordinates(rideId: bigint): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/rides/${rideId}/coordinates`)
      .pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Get GPS playback data for a ride
   */
  public getGpsPlaybackData(rideId: string | number): Observable<GpsPlaybackData> {
    return this.http
      .get<GpsPlaybackData>(`${this.baseUrl}/rides/gps-playback/${rideId}`)
      .pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Get dashboard charts data
   */
  public getDashboardCharts(locationId: number): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/Charts/GetDashboardCharts/${locationId}`, {
        locationId: locationId,
      })
      .pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Get dashboard statistics
   */
  public getDashboardStatistic(locationId: number): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/Charts/GetDashboardStatistic/${locationId}`, {
        locationId: locationId,
      })
      .pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Get ride by criteria (from authentication service)
   */
  public getRide(data: any): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/rides/get`, data)
      .pipe(retry(1), catchError(this.handleError));
  }

  /**
   * Check if service is available (health check)
   */
  public checkServiceHealth(): Observable<boolean> {
    return this.http.get(`${this.baseUrl}/health`).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }
}
